<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
                            <div class="d-flex align-items-center user-member__title justify-content-center mr-sm-25">
                                <h4 class="text-capitalize fw-500 breadcrumb-title">
                                    Produk Shop
                                </h4>
                            </div>
                        </div>
                        <div class="action-btn">
                            <router-link :to="`/shop`" class="btn px-15 btn-secondary text-white mr-2">
                                <i class="fas fa-arrow-left fs-16"></i>
                                Kembali
                            </router-link>
                            <a v-on:click="toggleModal('new_product')" class="btn px-15 btn-primary text-white">
                                <i class="fas fa-plus fs-16"></i>
                                Tambah Produk
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="productQuota === -1" class="alert alert-success" role="alert">
                <div class="alert-content">
                    <p>Batas produk maksimal anda adalah <b>Unlimited</b> produk</p>
                </div>
            </div>
            <div v-else-if="productQuota > 0" class="alert alert-primary" role="alert">
                <div class="alert-content">
                    <p>Batas produk maksimal anda adalah <b>{{ productQuota }}</b> produk</p>
                </div>
            </div>

            <!-- Donation campaign card (special product type) -->
            <DonationCampaignCard
                v-if="donationLoaded"
                :shop-id="shopId"
                :shop-slug="shopSlug"
                :settings="donationSettings"
                :stats="donationStats"
                @edit="toggleModal('donation_settings')"
            />

            <!-- LMS course card -->
            <LMSCourseCard
                v-if="lmsLoaded"
                :shop-id="shopId"
                :shop-slug="shopSlug"
                :settings="lmsSettings"
                :stats="lmsStats"
                :courses="lmsCourses"
                @edit="toggleModal('lms_settings')"
                @add-course="openNewCourse"
                @edit-course="openEditCourse"
                @delete-course="deleteCourse"
            />

            <div class="row">
                <div class="col-lg-12">
                    <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
                        <ProductTable :shop-id="shopId" @edit-product="editProduct" @delete-product="deleteProduct" />
                    </div>
                </div>
            </div>
            <modal name="new_product" :height="'auto'" :adaptive="true" :scrollable="true">
                <div style="margin: 20px">
                    <ProductForm :shop-id="shopId" @close="closeModal" @saved="refreshTable" />
                </div>
            </modal>
            <modal name="edit_product" :height="'auto'" :adaptive="true" :scrollable="true">
                <div style="margin: 20px">
                    <ProductForm :shop-id="shopId" :product-id="selectedProductId" @close="closeModal" @saved="refreshTable" />
                </div>
            </modal>
            <modal name="donation_settings" :height="'auto'" :adaptive="true" :scrollable="true">
                <div style="margin: 20px">
                    <DonationSettingsModal :shop-id="shopId" @close="closeModal" @saved="loadDonation" />
                </div>
            </modal>
            <modal name="lms_settings" :height="'auto'" :adaptive="true" :scrollable="true">
                <div style="margin: 20px">
                    <LMSSettingsModal :shop-id="shopId" @close="closeModal" @saved="loadLMS" />
                </div>
            </modal>
            <modal name="course_form" :height="'auto'" :adaptive="true" :scrollable="true">
                <div style="margin: 20px">
                    <CourseForm :shop-id="shopId" :course-id="selectedCourseId" @close="closeModal" @saved="loadLMS" />
                </div>
            </modal>
        </div>
    </div>
</template>

<script>
    import ProductTable from "@/components/tables/ShopProduct.vue";
    import ProductForm from "@/components/forms/shop/ProductForm.vue";
    import DonationCampaignCard from "@/components/shop/DonationCampaignCard.vue";
    import DonationSettingsModal from "@/components/shop/DonationSettingsModal.vue";
    import LMSCourseCard from "@/components/shop/LMSCourseCard.vue";
    import LMSSettingsModal from "@/components/shop/LMSSettingsModal.vue";
    import CourseForm from "@/components/shop/CourseForm.vue";

    export default {
        components: {
            ProductTable,
            ProductForm,
            DonationCampaignCard,
            DonationSettingsModal,
            LMSCourseCard,
            LMSSettingsModal,
            CourseForm,
        },
        data() {
            return {
                shopId: null,
                shopSlug: '',
                productQuota: "",
                selectedProductId: null,
                donationLoaded: false,
                donationSettings: { is_enabled: false, title: 'Donasi', description: '', target_amount: 0 },
                donationStats: { total_amount: 0, total_count: 0 },
                lmsLoaded: false,
                lmsSettings: { is_enabled: false },
                lmsStats: { total_courses: 0, total_count: 0, total_amount: 0 },
                lmsCourses: [],
                selectedCourseId: null,
            };
        },
        mounted() {
            this.shopId = parseInt(this.$route.params.shopId);
            this.productQuota = this.$store.state.auth.package?.max_shop_product ?? null;
            this.loadShop();
            this.loadDonation();
            this.loadLMS();
        },
        methods: {
            async loadShop() {
                try {
                    const r = await this.$store.dispatch('shop/shop_detail', this.shopId);
                    this.shopSlug = r.data.data.shop.slug || '';
                } catch (e) { /* noop */ }
            },
            async loadDonation() {
                try {
                    const [settingsR, statsR] = await Promise.all([
                        this.$store.dispatch('shop_donation/donation_get_settings', this.shopId),
                        this.$store.dispatch('shop_donation/donation_stats', this.shopId),
                    ]);
                    const s = settingsR.data.data.settings;
                    this.donationSettings = {
                        is_enabled: s.is_enabled === 1,
                        title: s.title || 'Donasi',
                        description: s.description || '',
                        target_amount: s.target_amount || 0,
                    };
                    this.donationStats = statsR.data.data || { total_amount: 0, total_count: 0 };
                } catch (e) {
                    this.donationSettings = { is_enabled: false, title: 'Donasi', description: '', target_amount: 0 };
                    this.donationStats = { total_amount: 0, total_count: 0 };
                }
                this.donationLoaded = true;
            },
            async loadLMS() {
                try {
                    const [settingsR, statsR, coursesR] = await Promise.all([
                        this.$store.dispatch('shop_lms/lms_get_settings', this.shopId),
                        this.$store.dispatch('shop_lms/lms_stats', this.shopId),
                        this.$store.dispatch('shop_lms/lms_list_courses', { shopId: this.shopId, params: {} }),
                    ]);
                    const s = settingsR.data.data.settings;
                    this.lmsSettings = { is_enabled: s.is_enabled === 1 };
                    this.lmsStats = statsR.data.data || { total_courses: 0, total_count: 0, total_amount: 0 };
                    this.lmsCourses = coursesR.data.data.courses || [];
                } catch (e) {
                    this.lmsSettings = { is_enabled: false };
                    this.lmsStats = { total_courses: 0, total_count: 0, total_amount: 0 };
                    this.lmsCourses = [];
                }
                this.lmsLoaded = true;
            },
            openNewCourse() {
                this.selectedCourseId = null;
                this.toggleModal('course_form');
            },
            openEditCourse(id) {
                this.selectedCourseId = id;
                this.toggleModal('course_form');
            },
            async deleteCourse(id) {
                if (!confirm('Hapus kursus ini?')) return;
                try {
                    await this.$store.dispatch('shop_lms/lms_delete_course', id);
                    this.$toast.success('Kursus dihapus');
                    this.loadLMS();
                } catch (e) {
                    this.$toast.error(e.response?.data?.message || 'Gagal menghapus');
                }
            },
            toggleModal(param) {
                this.$modal.toggle(param);
            },
            closeModal() {
                this.$modal.hide('new_product');
                this.$modal.hide('edit_product');
                this.$modal.hide('donation_settings');
                this.$modal.hide('lms_settings');
                this.$modal.hide('course_form');
            },
            editProduct(productId) {
                this.selectedProductId = productId;
                this.toggleModal('edit_product');
            },
            deleteProduct(productId) {
                if (confirm('Apakah anda yakin ingin menghapus produk ini?')) {
                    this.$store.dispatch('shop_product/shop_product_delete', productId)
                        .then(() => {
                            this.$toast.success('Produk berhasil dihapus');
                            this.refreshTable();
                        })
                        .catch(err => {
                            this.$toast.error(err.response?.data?.message || 'Gagal menghapus produk');
                        });
                }
            },
            refreshTable() {
                this.$root.$emit('refresh-product-table');
                this.closeModal();
            },
        },
    };
</script>
